/**
 * Pathname predicates shared by Session.tsx and the global axios interceptor.
 *
 * Two route categories opt out of strict auth handling:
 *
 *  - `isSessionEstablishmentExempt`: pages we render before a session
 *    exists (registration flows, password reset, verify-email, etc).
 *    Session.tsx skips its user-fetch on these and the interceptor must
 *    not bounce a 403 to login from these — there's no session to expire.
 *
 *  - `isOptionalAuthRoute`: `/pub/*` pages that can be visited
 *    anonymously OR while logged in. Same treatment: a 403 from a query
 *    fired by the page must not redirect to login because the page is
 *    valid for anonymous visitors.
 *
 * Keeping the predicates here lets the interceptor honor the same intent
 * Session.tsx expresses, instead of redirecting on every 403 and undoing
 * the route-aware gating upstream.
 */

export const isSessionEstablishmentExemptPath = (pathname: string): boolean =>
  pathname.startsWith('/invitation') ||
  pathname.startsWith('/register/provider/setup') ||
  pathname.startsWith('/register/provider/sourced') ||
  pathname.startsWith('/register/rep/setup') ||
  // Registration with mandatory email verification does not log the user in, so
  // this page is reached with a csrftoken cookie but no session. Listed
  // route-by-route rather than as a `/register` prefix because
  // `/register/complete` is the opposite case — social signup lands there
  // authenticated and depends on the user fetch.
  pathname.startsWith('/register/email-confirmation') ||
  pathname.startsWith('/password-reset') ||
  pathname.includes('/password-reset/') ||
  pathname.startsWith('/verify-email') ||
  pathname === '/forgot-password' ||
  pathname === '/reset-password' ||
  pathname.startsWith('/swag') ||
  pathname.startsWith('/endorsement');

export const isOptionalAuthRoutePath = (pathname: string): boolean =>
  pathname.startsWith('/pub') ||
  // The 1099 invitation page is valid both ways: anonymous (following the invite
  // link) and signed in (having just come back from Google), where it needs the
  // user to switch into "finish registration" mode. It stays in the
  // session-establishment list above so an anonymous visit never bounces.
  pathname.startsWith('/register/provider/sourced');

/**
 * True when the current page tolerates an unauthenticated state, either
 * because the user hasn't established a session yet or because the route
 * is public-by-design. Used by the global axios interceptor to skip the
 * 403 → session-expired redirect.
 */
export const isAnonymousTolerantPath = (pathname: string): boolean =>
  isSessionEstablishmentExemptPath(pathname) || isOptionalAuthRoutePath(pathname);
